// src/components/admin/TabIntegraciones.js
// Configuración de integraciones externas (marketplace, etc.). Solo SuperAdmin.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Stack, Chip, Alert, Button,
  TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
  CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import EditIcon from '@mui/icons-material/Edit';
import BoltIcon from '@mui/icons-material/Bolt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import {
  listarIntegraciones, actualizarIntegracion, probarIntegracion,
} from '../../services/aliadosApi';
import EditorTextosLegales from './EditorTextosLegales';
import EditorEfectoEstacional from './EditorEfectoEstacional';
import EditorConfigCanje   from './EditorConfigCanje';
import PanelTestAfiliados  from './PanelTestAfiliados';
import PanelTestAliados    from './PanelTestAliados';

export default function TabIntegraciones({ onToast }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);

  const [edit, setEdit]           = useState(null);   // integ en edición
  const [form, setForm]           = useState({ baseUrl: '', tipoAuth: 'NONE', credencial: '', activo: false, cambioCredencial: false });
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando]   = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listarIntegraciones();
      setItems(Array.isArray(r) ? r : []);
    } catch (e) {
      onToast?.(e.message || 'No se pudo cargar el listado.', 'error');
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirEdicion = (i) => {
    setEdit(i);
    setForm({
      baseUrl: i.baseUrl || '',
      tipoAuth: i.tipoAuth || 'NONE',
      credencial: '',   // vacío = no tocar; si se cambia, sí se envía
      activo: !!i.activo,
      cambioCredencial: false,
    });
  };

  const cerrarEdicion = () => {
    setEdit(null);
    setForm({ baseUrl: '', tipoAuth: 'NONE', credencial: '', activo: false, cambioCredencial: false });
  };

  const guardar = async () => {
    if (!edit) return;
    setGuardando(true);
    try {
      const dto = {
        baseUrl: form.baseUrl,
        tipoAuth: form.tipoAuth,
        activo: form.activo,
      };
      if (form.cambioCredencial) dto.credencial = form.credencial;   // '' limpia; texto cifra
      await actualizarIntegracion(edit.clave, dto);
      onToast?.(`Integración ${edit.clave} actualizada.`, 'success');
      cerrarEdicion();
      cargar();
    } catch (e) {
      onToast?.(e.message || 'No se pudo guardar.', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const probar = async (clave) => {
    setProbando(true);
    try {
      const r = await probarIntegracion(clave);
      if (r?.ok) {
        onToast?.(`Conexión OK (HTTP ${r.status}).`, 'success');
      } else {
        onToast?.(`Prueba: HTTP ${r?.status ?? '—'} · ${r?.mensaje || 'sin detalle'}`, 'warning');
      }
    } catch (e) {
      onToast?.(e.message || 'No se pudo probar la conexión.', 'error');
    } finally {
      setProbando(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <SettingsInputAntennaIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a4d2e' }}>Integraciones externas</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Recargar">
          <IconButton onClick={cargar} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
        Configuración de APIs externas que consume ecolab. Las credenciales se guardan cifradas
        (AES-256-GCM) y nunca se muestran en pantalla. Solo SuperAdmin puede editar.
      </Alert>

      {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>}
      {!loading && items.length === 0 && (
        <Alert severity="warning">No hay integraciones registradas.</Alert>
      )}

      <Grid container spacing={2}>
        {items.map(i => (
          <Grid item xs={12} md={6} key={i.integracionID}>
            <Paper elevation={0} sx={{
              p: 2, border: '1px solid #e0e0e0', borderRadius: 2, height: '100%',
              display: 'flex', flexDirection: 'column', gap: 1,
            }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontWeight: 700 }}>{i.nombre}</Typography>
                {i.activo
                  ? <Chip label="Activa"    size="small" color="success" />
                  : <Chip label="Inactiva"  size="small" />
                }
                <Box sx={{ flex: 1 }} />
                <Chip label={i.clave} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
              </Stack>
              {i.descripcion && (
                <Typography variant="caption" sx={{ color: '#666' }}>{i.descripcion}</Typography>
              )}
              <Divider sx={{ my: 1 }} />
              <Grid container sx={{ fontSize: '0.85rem' }}>
                <Grid item xs={4} sx={{ color: '#666' }}>Base URL</Grid>
                <Grid item xs={8} sx={{ wordBreak: 'break-all' }}>{i.baseUrl || <em style={{color:'#c62828'}}>Sin configurar</em>}</Grid>
                <Grid item xs={4} sx={{ color: '#666' }}>Auth</Grid>
                <Grid item xs={8}>{i.tipoAuth}</Grid>
                <Grid item xs={4} sx={{ color: '#666' }}>Credencial</Grid>
                <Grid item xs={8}>
                  {i.credencialConfigurada
                    ? <Chip label="Configurada" size="small" color="success" variant="outlined" />
                    : <Chip label="No configurada" size="small" color="warning" variant="outlined" />
                  }
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1} sx={{ mt: 'auto', pt: 1 }}>
                <Button size="small" variant="outlined" startIcon={<EditIcon />}
                  onClick={() => abrirEdicion(i)}>Editar</Button>
                {i.clave === 'MARKETPLACE_API' && (
                  <Button size="small" variant="text" startIcon={probando ? <CircularProgress size={14} /> : <BoltIcon />}
                    onClick={() => probar(i.clave)} disabled={probando}>
                    Probar conexión
                  </Button>
                )}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Dialog de edición */}
      <Dialog open={!!edit} onClose={cerrarEdicion} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Editar integración</Typography>
            {edit && <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>{edit.nombre}</Typography>}
          </Box>
          <IconButton onClick={cerrarEdicion} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important' }}>
          {edit && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Base URL"
                  value={form.baseUrl}
                  onChange={e => setForm(p => ({ ...p, baseUrl: e.target.value }))}
                  placeholder="https://market.fenagro-oficial.org/api"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de autenticación</InputLabel>
                  <Select value={form.tipoAuth} label="Tipo de autenticación"
                    onChange={e => setForm(p => ({ ...p, tipoAuth: e.target.value }))}>
                    <MenuItem value="NONE">Sin autenticación</MenuItem>
                    <MenuItem value="BEARER">Bearer token</MenuItem>
                    <MenuItem value="API_KEY">API Key</MenuItem>
                    <MenuItem value="BASIC">Basic auth</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado</InputLabel>
                  <Select value={form.activo ? 'true' : 'false'} label="Estado"
                    onChange={e => setForm(p => ({ ...p, activo: e.target.value === 'true' }))}>
                    <MenuItem value="true">Activa</MenuItem>
                    <MenuItem value="false">Inactiva</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Credencial</Typography>
                  {edit.credencialConfigurada && !form.cambioCredencial && (
                    <Chip label="Ya configurada · no se toca" size="small" color="success" variant="outlined" />
                  )}
                </Stack>
                {form.cambioCredencial ? (
                  <>
                    <TextField fullWidth size="small" type="password"
                      label={form.tipoAuth === 'BASIC' ? 'usuario:contraseña' : 'Token / clave'}
                      value={form.credencial}
                      onChange={e => setForm(p => ({ ...p, credencial: e.target.value }))}
                      helperText="Vacío para limpiar la credencial guardada."
                    />
                    <Button size="small" variant="text" sx={{ mt: 0.5 }}
                      onClick={() => setForm(p => ({ ...p, cambioCredencial: false, credencial: '' }))}>
                      Cancelar cambio
                    </Button>
                  </>
                ) : (
                  <Button size="small" variant="outlined"
                    onClick={() => setForm(p => ({ ...p, cambioCredencial: true }))}>
                    {edit.credencialConfigurada ? 'Cambiar credencial' : 'Establecer credencial'}
                  </Button>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cerrarEdicion}>Cancelar</Button>
          <Button variant="contained" color="success"
            startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={guardar} disabled={guardando}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Editor de textos legales (T&C, políticas) */}
      <EditorTextosLegales onToast={onToast} />

      {/* Efecto estacional del sitio público (nieve / estrellas) */}
      <EditorEfectoEstacional onToast={onToast} />

      {/* Configuración de códigos de canje */}
      <EditorConfigCanje onToast={onToast} />

      {/* Panel oculto de afiliados de prueba (mecanismo test) */}
      <PanelTestAfiliados onToast={onToast} />

      {/* Panel oculto de aliados de prueba (mecanismo test) */}
      <PanelTestAliados onToast={onToast} />
    </Box>
  );
}
